import connect from 'components/Shared/connect';
import {
  getQuantity,
  getFullProductId,
  getIsFamily,
  isPurchasable,
  getIsAddonProduct,
  getConfiguratorVariablesPure,
  getConfiguratorAttributesPure,
  getProductTicket,
  getStockMeta,
  showDeliveryInfo
} from 'views/ProductPage/selector';
import { IQuantity } from 'models/IQuantity';
import { isMultiQuantity } from 'components/Basket/selector';
import { Reducer, useReducer, useEffect } from 'react';
import { validate } from 'views/ProductPage/validateAddToCart';
import { productPageAddToCart } from 'views/ProductPage/actions';
import { AddItemRequest } from 'models/AddItemRequest';
import { AddItemResponse, AddItemRecommendations } from 'models/AddItemResponse';
import { Quantity } from './quantity';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { AttributeType } from 'models/AttributeType';
import { isMobileLayout } from 'components/Shared/responsive';
import { usePrevious } from 'hooks';
import { CartComponent } from './cartComponent';
import { _DeliveryInformation as DeliveryInformation } from 'views/ProductPage/productInfo/deliveryInformation/';
import { rm } from 'utils';
import { getConfigurator } from 'views/ProductPage/configurators/selector';
import { notifyAddToCart } from 'components/Shared/NotifyLink';
import { MergedProps as DeliveryInformationProps } from 'views/ProductPage/productInfo/deliveryInformation';
import { LoadingStatus } from 'models/LoadingStatus';
import { useDispatch, useSelector } from 'react-redux';
import { ProductDetailedMeta } from 'models/ProductMeta';
import { hasSelectedAddonService } from 'views/ProductPage/addonServices/selector';
import { GtmAddToCartLocation, GtmProduct } from 'store/googleTagManager/types';
import { AddToCartResponse, useECPAddToCart } from 'components/Cart/actions';
import { selectShouldUseGecpHeader } from 'store/appShell/selector';
import { GtmEditQuantity } from 'store/googleTagManager/actions';
import { AddonGroup } from 'models/AddonGroup';

interface OwnProps {
  className?: string;
  inDrawer?: boolean;
  gtmProduct?: GtmProduct;
}

interface MergedProps
  extends OwnProps,
    Pick<DeliveryInformationProps, 'meta' | 'showDeliveryInfo'> {
  fullProductId: string;
  isFamily: boolean;
  quantity: IQuantity;
  customOptions: ConfiguratorBase['attributes'];
  addItemToCart: (
    item: AddItemRequest,
    btnLocation: GtmAddToCartLocation
  ) => Promise<AddItemResponse>;
  isDesktop?: boolean;
  isPurchasable: boolean;
  isAddon?: boolean;
  status?: number;
  ticket: string;
  addons?: Record<string, AddonGroup>;
  configuratorVariables?: Record<string, string>;
}

interface OwnState {
  amount: number;
  preferredAmount: number;
  multiAmount: number;
  error?: string[];
}

type Action =
  | { type: 'openErrorModal'; message: string | string[] }
  | { type: 'closeErrorModal' }
  | { type: 'updateAmount'; amount: number; multiAmount?: number }
  | { type: 'updatePreferredAmount'; preferredAmount: number };

const getInitialState = (quantity: IQuantity): OwnState => {
  return {
    amount: isMultiQuantity(quantity) ? 0 : quantity.lowest,
    preferredAmount: 0,
    multiAmount: isMultiQuantity(quantity) ? quantity.factor : 0
  };
};

const ownReducer: Reducer<OwnState, Action> = (state, action) => {
  switch (action.type) {
    case 'openErrorModal':
      return { ...state, error: Array.isArray(action.message) ? action.message : [action.message] };
    case 'closeErrorModal':
      return { ...state, error: undefined };
    case 'updateAmount': {
      return {
        ...state,
        amount: action.amount,
        multiAmount: action.multiAmount ?? state.multiAmount
      };
    }
    case 'updatePreferredAmount': {
      return {
        ...state,
        preferredAmount: action.preferredAmount
      };
    }
    default:
      return state;
  }
};

interface HoverState {
  isOpen: boolean;
  addItemRecommendations?: AddItemRecommendations;
  message?: string;
}

type HoverAction =
  | { type: 'close' }
  | {
      type: 'setData';
      addItemRecommendations?: AddItemRecommendations;
      message?: string;
    };

const hoverInitialState = {
  isOpen: false
};

export const hoverReducer: Reducer<HoverState, HoverAction> = (state, action) => {
  switch (action.type) {
    case 'close': {
      return {
        ...state,
        isOpen: false
      };
    }
    case 'setData': {
      return {
        ...state,
        ...action,
        isOpen: true
      };
    }
    default:
      return state;
  }
};

const getBaseProductId = (id?: string) => id?.split('-')[0];

const mandatoryAddonServiceValid = (
  mandatoryAddonService: boolean,
  hasSelectedAddonServices: boolean
) => {
  if (mandatoryAddonService) {
    return hasSelectedAddonServices;
  }
  return true;
};

interface UseAddToCart extends MergedProps {
  amount: number;
  hoverDispatch?: React.Dispatch<HoverAction>;
  dispatch?: React.Dispatch<Action>;
}

type UseAddToCartResponse = (btnLocation: GtmAddToCartLocation) => Promise<void>;

export const getResponseData = (response: AddItemResponse | AddToCartResponse): AddItemResponse => {
  if ('data' in response) {
    return response.data;
  }

  return response;
};

export const useAddToCart = ({
  amount,
  hoverDispatch,
  dispatch,
  fullProductId,
  ...props
}: UseAddToCart): UseAddToCartResponse => {
  const shouldUseGecpHeader = useSelector(selectShouldUseGecpHeader);
  const { addToCart: addItemToCart } = useECPAddToCart();

  return (btnLocation: GtmAddToCartLocation): Promise<void> => {
    const { isValid, message } = validate(props, amount);

    if (!isValid && dispatch) {
      return Promise.resolve(dispatch({ type: 'openErrorModal', message }));
    }

    notifyAddToCart(props.ticket);

    const addToCart: Promise<AddItemResponse | AddToCartResponse> = shouldUseGecpHeader
      ? addItemToCart({
          quantity: amount,
          productId: fullProductId,
          gtmBtnLocation: btnLocation,
          includeAddons: true,
          inDrawer: props.inDrawer,
          addons: props.addons,
          configuratorVariables: props.configuratorVariables
        })
      : props.addItemToCart({ id: fullProductId, quantity: amount }, btnLocation);

    return addToCart.then(getResponseData).then(({ addItemRecommendations, message }) => {
      if (hoverDispatch) {
        hoverDispatch({
          type: 'setData',
          addItemRecommendations,
          message
        });
      }
    });
  };
};

const getIsMandatoryAddonService = (service: unknown): service is ProductDetailedMeta => {
  return 'carriageMethod' in (service as ProductDetailedMeta);
};

export const _AddToCartSection = (props: MergedProps) => {
  const [state, dispatch] = useReducer(ownReducer, getInitialState(props.quantity));
  const [hoverState, hoverDispatch] = useReducer(hoverReducer, hoverInitialState);
  const addToCart = useAddToCart({ dispatch, hoverDispatch, amount: state.amount, ...props });
  const closeHover = () => hoverDispatch({ type: 'close' });
  const hasSelectedAddonServices = useSelector(hasSelectedAddonService);
  const previousProductId = usePrevious(props.fullProductId);
  const baseProductId = getBaseProductId(props.fullProductId);
  const previousBaseProductId = getBaseProductId(previousProductId);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (baseProductId !== previousBaseProductId) {
      dispatch({ type: 'updateAmount', amount: props.quantity.lowest, multiAmount: 0 });
      dispatch({ type: 'updatePreferredAmount', preferredAmount: 0 });
    }
  }, [baseProductId, previousBaseProductId, props.quantity.lowest]);

  useEffect(() => {
    if (hoverState.isOpen && previousProductId !== props.fullProductId) {
      closeHover();
    }
  }, [hoverState.isOpen, previousProductId, props.fullProductId]);

  useEffect(() => {
    if (previousProductId !== props.fullProductId && state.amount < props.quantity.lowest) {
      dispatch({
        type: 'updateAmount',
        amount: isMultiQuantity(props.quantity) ? 0 : props.quantity.lowest
      });
    }
  }, [previousProductId, props.fullProductId, props.quantity, state.amount]);

  if (props.isAddon) {
    return <p className={props.className}>{rm('product_addon_nosale')}</p>;
  }

  if (!props.isPurchasable) {
    return <DeliveryInformation meta={props.meta} showDeliveryInfo />;
  }
  const isMulti = isMultiQuantity(props.quantity);

  const updateAmount = (amount: number, multiAmount?: number, shouldUpdateInput?: boolean) => {
    dispatch({ type: 'updateAmount', amount, multiAmount });
    if (shouldUpdateInput) {
      dispatch({ type: 'updatePreferredAmount', preferredAmount: multiAmount || 0 });
    }
  };

  const dispatchQuantityGtm = (quantity: number, startQuantity: number) => {
    if (quantity !== startQuantity) {
      props.gtmProduct && reduxDispatch(GtmEditQuantity(props.gtmProduct, quantity, startQuantity));
    }
  };

  const updatePreferredAmount = (preferredAmount: number) => {
    dispatch({ type: 'updatePreferredAmount', preferredAmount });
  };

  const handleAdd = (btnLocation: GtmAddToCartLocation) => {
    return addToCart(btnLocation);
  };

  const quantityProps = {
    ...props.quantity,
    ...state
  };

  const quantity = (
    <Quantity
      key={baseProductId} // to reset arbitrary amount when navigation from one product to another
      {...quantityProps}
      updateAmount={updateAmount}
      updatePreferredAmount={updatePreferredAmount}
      dispatchQuantityEvent={dispatchQuantityGtm}
    />
  );

  const isMandatoryAddonService =
    getIsMandatoryAddonService(props.meta) && props.meta.mandatoryAddonService;
  const isValidMandatoryAddonService = mandatoryAddonServiceValid(
    isMandatoryAddonService,
    hasSelectedAddonServices
  );
  const isDisabled =
    props.status === LoadingStatus.Fetching ||
      !isValidMandatoryAddonService ||
      isMultiQuantity(props.quantity)
      ? state.amount <= 0 || state.preferredAmount <= 0
      : state.amount <= 0;

  const closeErrorModal = () => dispatch({ type: 'closeErrorModal' });
  const errorModalOpen = Boolean(state.error);

  const getDisabledInfo = () => {
    return isValidMandatoryAddonService
      ? undefined
      : rm('Installation_Required_Cart_Disabled_Info');
  };

  return (
    <CartComponent
      isFamily={props.isFamily}
      customOptions={props.customOptions}
      quantityData={props.quantity}
      errorModalOpen={errorModalOpen}
      closeErrorModal={closeErrorModal}
      className={props.className}
      isMulti={isMulti}
      isDesktop={props.isDesktop}
      inDrawer={props.inDrawer}
      quantity={quantity}
      fullProductId={props.fullProductId}
      isDisabled={isDisabled}
      handleAdd={handleAdd}
      closeHover={closeHover}
      error={state.error}
      hoverState={hoverState}
      meta={props.meta}
      showDeliveryInfo={props.showDeliveryInfo}
      numberOfSelectedProducts={state.amount}
      isDisabledInfo={getDisabledInfo()}
    />
  );
};

interface StateProps {
  values: { [key: string]: string };
  attributes: ConfiguratorBase['attributes'];
}

export const AddToCartSection = connect<MergedProps, OwnProps, StateProps>(
  state => ({
    quantity: getQuantity(state.currentPage),
    fullProductId: getFullProductId(state.currentPage),
    isFamily: getIsFamily(state.currentPage),
    isDesktop: !isMobileLayout(state),
    isPurchasable: isPurchasable(state),
    configurator: getConfigurator(state.currentPage),
    isAddon: getIsAddonProduct(state.currentPage),
    status: state.currentPage.status,
    ticket: getProductTicket(state),
    meta: getStockMeta(state),
    showDeliveryInfo: showDeliveryInfo(state.currentPage)
  }),
  dispatch => ({
    addItemToCart: (item: AddItemRequest, btnLocation: GtmAddToCartLocation) =>
      dispatch(productPageAddToCart({ item, gtmBtnLocation: btnLocation }))
  }),
  ({ configurator, ...state }, dispatch, props) => {
    const values = getConfiguratorVariablesPure(configurator);
    const attributes = getConfiguratorAttributesPure(configurator);

    return {
      ...state,
      ...dispatch,
      ...props,
      customOptions: attributes.filter(
        a =>
          a.type !== AttributeType.Input && a.type !== AttributeType.NumericInput && !values[a.id]
      )
    };
  }
)(_AddToCartSection);
